type AppleAuthResponse = {
  authorization?: {
    id_token?: string;
  };
};

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (options: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<AppleAuthResponse>;
      };
    };
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            use_fedcm_for_prompt?: boolean;
            cancel_on_tap_outside?: boolean;
            itp_support?: boolean;
          }) => void;
          prompt: (
            momentListener?: (notification: PromptMomentNotification) => void,
          ) => void;
          renderButton: (
            element: HTMLElement,
            options: {
              type?: "standard" | "icon";
              size?: "large" | "medium" | "small";
              theme?: "outline" | "filled_blue" | "filled_black";
              text?: string;
              shape?: "rectangular" | "pill" | "circle" | "square";
              width?: number;
            },
          ) => void;
          cancel: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }

  interface PromptMomentNotification {
    isDisplayMoment: () => boolean;
    isDisplayed: () => boolean;
    isNotDisplayed: () => boolean;
    getNotDisplayedReason: () => string;
    isSkippedMoment: () => boolean;
    getSkippedReason: () => string;
    isDismissedMoment: () => boolean;
    getDismissedReason: () => string;
    getMomentType: () => string;
  }
}

const APPLE_SCRIPT_ID = "apple-identity-services";
const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const GOOGLE_SCRIPT_ID = "google-identity-services";
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

const GSI_PROMPT_TIMEOUT_MS = 180_000;

// GSI injects an iframe/overlay with this id when the One Tap prompt is shown
const GSI_OVERLAY_ID = "credential_picker_container";

export function useSocialIdentity() {
  const config = useRuntimeConfig();

  const googleClientId = computed(() => config.public.googleClientId as string);
  const appleClientId = computed(() => config.public.appleClientId as string);

  // -------------------------------------------------------------------------
  // Shared helpers
  // -------------------------------------------------------------------------

  async function loadScript(id: string, src: string): Promise<void> {
    if (!import.meta.client) return;
    if (document.getElementById(id)) return;

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.id = id;
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Unable to load ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Watches for GSI's overlay container to be removed from the DOM.
   * Fires `onAbort` if the overlay disappears before the credential callback
   * resolves — meaning the user closed the prompt without signing in.
   *
   * Returns a cleanup function to disconnect the observer early (e.g. on success).
   */
  function watchForGSIAbort(onAbort: () => void): () => void {
    // Wait for GSI to inject its overlay first, then watch for its removal
    let removalObserver: MutationObserver | null = null;

    const injectionObserver = new MutationObserver(() => {
      const overlay = document.getElementById(GSI_OVERLAY_ID);
      if (!overlay) return;

      // Overlay appeared — now watch for it being removed
      injectionObserver.disconnect();

      removalObserver = new MutationObserver(() => {
        if (!document.getElementById(GSI_OVERLAY_ID)) {
          removalObserver?.disconnect();
          onAbort();
        }
      });

      removalObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });

    injectionObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      injectionObserver.disconnect();
      removalObserver?.disconnect();
    };
  }

  // -------------------------------------------------------------------------
  // Google — GSI with renderButton fallback
  // -------------------------------------------------------------------------

  function signInWithGoogleViaButton(clientId: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const container = document.createElement("div");
      container.style.cssText =
        "position:fixed;top:-9999px;left:-9999px;width:200px;height:40px;overflow:hidden;opacity:0;pointer-events:none;";
      document.body.appendChild(container);

      const cleanup = () => {
        try {
          document.body.removeChild(container);
        } catch {
          // Already removed
        }
      };

      // Watch for the popup window closing without a credential
      let popupCheckInterval: ReturnType<typeof setInterval> | null = null;
      let popupWindow: Window | null = null;

      const stopPopupWatch = () => {
        if (popupCheckInterval) {
          clearInterval(popupCheckInterval);
          popupCheckInterval = null;
        }
      };

      try {
        window.google!.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            stopPopupWatch();
            cleanup();
            if (response.credential) {
              resolve(response.credential);
            } else {
              reject(new Error("Google did not return a credential"));
            }
          },
          use_fedcm_for_prompt: false,
          cancel_on_tap_outside: false,
          itp_support: true,
        });

        window.google!.accounts.id.renderButton(container, {
          type: "standard",
          size: "large",
          theme: "outline",
          text: "signin_with",
          shape: "rectangular",
          width: 200,
        });

        requestAnimationFrame(() => {
          const btn =
            container.querySelector<HTMLElement>("[role=button]") ??
            container.querySelector<HTMLElement>("div[tabindex]") ??
            container.querySelector<HTMLElement>("iframe");

          if (!btn) {
            cleanup();
            reject(
              new Error(
                "Google sign-in button could not be rendered. Please try again.",
              ),
            );
            return;
          }

          // Capture the popup window reference so we can poll it for closure
          const originalOpen = window.open.bind(window);
          window.open = (...args) => {
            popupWindow = originalOpen(...args);
            window.open = originalOpen; // restore immediately
            return popupWindow;
          };

          btn.click();

          // Poll for the popup being closed by the user
          popupCheckInterval = setInterval(() => {
            if (popupWindow && popupWindow.closed) {
              stopPopupWatch();
              cleanup();
              reject(new Error("Google sign-in was cancelled"));
            }
          }, 300);
        });
      } catch (err) {
        stopPopupWatch();
        cleanup();
        reject(err);
      }
    });
  }

  async function signInWithGoogle(): Promise<string> {
    if (!import.meta.client) {
      throw new Error("Cannot sign in on the server");
    }

    if (!googleClientId.value) {
      throw new Error("Google client ID is not configured");
    }

    await loadScript(GOOGLE_SCRIPT_ID, GOOGLE_SCRIPT_SRC);

    if (!window.google?.accounts?.id) {
      throw new Error("Google Identity Services failed to load");
    }

    return new Promise<string>((resolve, reject) => {
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      let disconnectAbortWatcher: (() => void) | null = null;

      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        if (timeoutHandle) clearTimeout(timeoutHandle);
        disconnectAbortWatcher?.();
        fn();
      };

      window.google!.accounts.id.initialize({
        client_id: googleClientId.value,
        callback: (response) => {
          settle(() => {
            if (response.credential) {
              resolve(response.credential);
            } else {
              reject(new Error("Google did not return a credential"));
            }
          });
        },
        use_fedcm_for_prompt: false,
        cancel_on_tap_outside: true,
        itp_support: true,
      });

      timeoutHandle = setTimeout(() => {
        settle(() => {
          window.google?.accounts.id.cancel();
          reject(new Error("Google sign-in timed out. Please try again."));
        });
      }, GSI_PROMPT_TIMEOUT_MS);

      window.google!.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed()) {
          // Prompt suppressed — fall back to button popup which has its own abort detection
          settle(() => {
            signInWithGoogleViaButton(googleClientId.value)
              .then(resolve)
              .catch(reject);
          });
        } else if (notification.isSkippedMoment()) {
          settle(() => reject(new Error("Google sign-in was cancelled")));
        } else if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          if (reason === "credential_returned") return;
          settle(() => reject(new Error("Google sign-in was cancelled")));
        } else if (notification.isDisplayed()) {
          // Prompt is now visible — start watching for the user closing it
          // without completing sign-in (covers cases where no dismiss event fires)
          disconnectAbortWatcher = watchForGSIAbort(() => {
            settle(() => reject(new Error("Google sign-in was cancelled")));
          });
        }
      });
    });
  }

  // -------------------------------------------------------------------------
  // Apple
  // -------------------------------------------------------------------------

  async function signInWithApple(): Promise<string> {
    if (!import.meta.client) {
      throw new Error("Cannot sign in on the server");
    }

    if (!appleClientId.value) {
      throw new Error("Apple client ID is not configured");
    }

    await loadScript(APPLE_SCRIPT_ID, APPLE_SCRIPT_SRC);

    if (!window.AppleID?.auth) {
      throw new Error("Apple Sign in is unavailable");
    }

    window.AppleID.auth.init({
      clientId: appleClientId.value,
      scope: "name email",
      redirectURI: window.location.origin,
      usePopup: true,
    });

    const response = await window.AppleID.auth.signIn();
    const jwt = response.authorization?.id_token;

    if (!jwt) {
      throw new Error("Apple did not return an identity token");
    }

    return jwt;
  }

  return {
    signInWithGoogle,
    signInWithApple,
  };
}
